  import { getTenantConfig } from './auth.svelte';
  import { isDemoTenant } from './hospital-preview.svelte';
import type { Tab } from './navigation';
import { isTabLicensed, moduleForTab } from './navigation';
import { applyTenantBranding, clearTenantBranding, resolveDisplayName, type TenantBranding } from './branding.svelte';

export type TenantSnapshot = {
  tenantId: string;
  nombre: string;
  edicion: string | null;
  plan: string | null;
  estado: string | null;
  orgTipo: string | null;
  modulosActivos: string[] | null;
  branding: TenantBranding | null;
};

let _snapshot = $state<TenantSnapshot | null>(null);
let _loading = $state(false);
let _loadedFor = $state<string | null>(null);

export const tenantCtx = {
  get snapshot() { return _snapshot; },
  get loading() { return _loading; },
  get modulosActivos() { return _snapshot?.modulosActivos ?? null; },
  get edicion() { return _snapshot?.edicion ?? null; },
  get plan() { return _snapshot?.plan ?? null; },
  get estado() { return _snapshot?.estado ?? null; },
  get orgTipo() { return _snapshot?.orgTipo ?? null; },
  get nombre() { return _snapshot?.nombre ?? null; },
  get branding() { return _snapshot?.branding ?? null; },
  /** true durante la prueba gratuita: acceso total a todos los módulos. */
  get isTrial() { return _snapshot?.estado === 'trial'; },
  /** Nombre comercial para mensajes al paciente (marca del tenant). */
  get displayName() {
    return resolveDisplayName(_snapshot?.branding, _snapshot?.nombre, _snapshot?.tenantId ?? '');
  },
  get isHospitalEdition() {
    const e = _snapshot?.edicion;
    const o = _snapshot?.orgTipo;
    return e === 'private-hospital' || e === 'public-hospital' || o === 'hospital-privado';
  },
  get isClinicEdition() {
    const e = _snapshot?.edicion;
    return e === 'solo-clinic' || e === 'clinic-network' || (!!_snapshot?.orgTipo?.startsWith('clinica-') && !this.isHospitalEdition);
  },
  tabLicensed(tab: Tab): boolean {
    return isTabLicensed(tab, _snapshot?.modulosActivos);
  },
  hasModule(mod: string): boolean {
    const mods = _snapshot?.modulosActivos;
    if (!mods?.length) return true;
    return mods.includes(mod);
  },
};

export async function loadTenantContext(tenantId: string): Promise<void> {
  if (_loadedFor === tenantId && _snapshot) return;
  _loading = true;
  try {
    const cfg = await getTenantConfig(tenantId);
    const estado = (cfg?.estado as string) ?? null;
    _snapshot = {
      tenantId,
      nombre: cfg?.nombre ?? tenantId,
      edicion: cfg?.edicion ?? null,
      plan: cfg?.plan ?? null,
      estado,
      orgTipo: (cfg?.config?.tipoOrganizacion as string) ?? null,
      // Durante la prueba gratuita se habilita el acceso a todos los módulos.
      modulosActivos: estado === 'trial' ? [] : (cfg?.modulosActivos ?? null),
      branding: cfg?.branding ?? null,
    };
    applyTenantBranding(_snapshot.branding, _snapshot.orgTipo);
    _loadedFor = tenantId;
  } catch {
    const planByTenant: Record<string, string> = {
      'hosp-demo-starter': 'hospital-starter',
      'hosp-demo-pro': 'hospital-pro',
      'hosp-demo-enterprise': 'hospital-enterprise',
    };
    const plan = planByTenant[tenantId] ?? (isDemoTenant(tenantId) ? 'hospital-pro' : null);
    _snapshot = {
      tenantId,
      nombre: tenantId,
      edicion: plan ? 'private-hospital' : null,
      plan,
      estado: null,
      orgTipo: plan ? 'hospital-privado' : null,
      modulosActivos: plan ? [] : [],
      branding: null,
    };
    applyTenantBranding(_snapshot.branding, _snapshot.orgTipo);
    _loadedFor = tenantId;
  } finally {
    _loading = false;
  }
}

export function clearTenantContext(): void {
  _snapshot = null;
  _loadedFor = null;
  clearTenantBranding();
}

/** Fuerza recarga del tenant tras cambios de branding. */
export async function reloadTenantContext(): Promise<void> {
  const id = _loadedFor;
  _loadedFor = null;
  if (id) await loadTenantContext(id);
}

/** Etiqueta legible del plan comercial. */
export function planLabel(slug: string | null): string | null {
  if (!slug) return null;
  const map: Record<string, string> = {
    'clinica-solo': 'Clínica Solo',
    'clinica': 'Clínica',
    'clinica-plus': 'Clínica Plus',
    'hospital-starter': 'Hospital Starter',
    'hospital-pro': 'Hospital Pro',
    'hospital-enterprise': 'Hospital Enterprise',
  };
  return map[slug] ?? slug;
}

export function moduleLabel(mod: string): string {
  const map: Record<string, string> = {
    admision: 'Admisión',
    hospitalizacion: 'Hospitalización',
    expediente: 'Expediente',
    facturacion: 'Facturación',
    laboratorio: 'Laboratorio',
    imagenologia: 'Imagenología',
    quirofano: 'Quirófano',
    'farmacia-hospitalaria': 'Farmacia hosp.',
    bodega: 'Bodega',
    tesoreria: 'Tesorería',
    nomina: 'Nómina',
    contabilidad: 'Contabilidad',
    'reportes-mspas': 'MSPAS',
    citas: 'Agenda',
    veterinaria: 'Veterinaria',
    optica: 'Óptica',
  };
  return map[mod] ?? mod;
}

export { moduleForTab };
